import { useState, useEffect, useCallback } from 'react';
import { userDataPersistence, UserDataItem, SyncResult, DataValidationResult } from '../services/UserDataPersistenceService';
import { supabase } from '../lib/supabase';
import { errorLogger } from '../utils/errorLogger';

export interface UseUserDataPersistenceOptions {
  autoSync?: boolean;
  syncInterval?: number; // in milliseconds
  encrypt?: boolean;
  validateOnSave?: boolean;
}

export interface UserDataPersistenceState {
  isSyncing: boolean;
  lastSyncTime: string | null;
  pendingItems: number;
  syncedItems: number;
  failedItems: number;
  error: string | null;
}

export function useUserDataPersistence(options: UseUserDataPersistenceOptions = {}) {
  const {
    autoSync = true,
    syncInterval = 30000, // 30 seconds
    encrypt = true,
    validateOnSave = true
  } = options;

  const [state, setState] = useState<UserDataPersistenceState>({
    isSyncing: false,
    lastSyncTime: null,
    pendingItems: 0,
    syncedItems: 0,
    failedItems: 0,
    error: null
  });

  const [currentUser, setCurrentUser] = useState<any>(null);

  // Get current user on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setCurrentUser(user);
      } catch (error) {
        errorLogger.logError(
          error instanceof Error ? error : new Error(String(error)),
          'useUserDataPersistence',
          { action: 'get_current_user' },
          'medium'
        );
      }
    };

    getCurrentUser();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setCurrentUser(session?.user || null);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Update sync status periodically
  useEffect(() => {
    const updateSyncStatus = () => {
      const syncStatus = userDataPersistence.getSyncStatus();
      setState(prev => ({
        ...prev,
        isSyncing: syncStatus.isSyncing,
        lastSyncTime: syncStatus.lastSyncTime,
        pendingItems: syncStatus.pendingItems,
        syncedItems: syncStatus.syncedItems,
        failedItems: syncStatus.failedItems
      }));
    };

    updateSyncStatus();
    const interval = setInterval(updateSyncStatus, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Auto-sync functionality
  useEffect(() => {
    if (!autoSync || !currentUser) return;

    const performAutoSync = async () => {
      try {
        setState(prev => ({ ...prev, isSyncing: true, error: null }));
        const result = await userDataPersistence.syncWithSupabase();
        
        if (!result.success) {
          setState(prev => ({ 
            ...prev, 
            error: `Sync failed: ${result.errors.join(', ')}` 
          }));
        }
      } catch (error) {
        setState(prev => ({ 
          ...prev, 
          error: error instanceof Error ? error.message : String(error) 
        }));
      } finally {
        setState(prev => ({ ...prev, isSyncing: false }));
      }
    };

    const interval = setInterval(performAutoSync, syncInterval);
    return () => clearInterval(interval);
  }, [autoSync, syncInterval, currentUser]);

  // Save user data
  const saveUserData = useCallback(async (
    type: UserDataItem['type'],
    data: any,
    saveOptions: {
      immediateSync?: boolean;
      encrypt?: boolean;
      backup?: boolean;
    } = {}
  ): Promise<string | null> => {
    if (!currentUser) {
      const error = 'User not authenticated';
      setState(prev => ({ ...prev, error }));
      errorLogger.logError(
        new Error(error),
        'useUserDataPersistence',
        { action: 'save_user_data', type },
        'high'
      );
      return null;
    }

    try {
      setState(prev => ({ ...prev, error: null }));

      // Validate data if requested
      if (validateOnSave) {
        const validation = userDataPersistence.validateUserData(data, type);
        if (!validation.isValid) {
          const error = `Validation failed: ${validation.errors.join(', ')}`;
          setState(prev => ({ ...prev, error }));
          return null;
        }
      }

      const dataId = await userDataPersistence.saveUserData(
        currentUser.id,
        type,
        data,
        {
          immediateSync: saveOptions.immediateSync,
          encrypt: saveOptions.encrypt ?? encrypt,
          backup: saveOptions.backup
        }
      );

      return dataId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setState(prev => ({ ...prev, error: errorMessage }));
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'useUserDataPersistence',
        { action: 'save_user_data', type },
        'high'
      );
      return null;
    }
  }, [currentUser, validateOnSave, encrypt]);

  // Get user data
  const getUserData = useCallback(async (dataId: string): Promise<UserDataItem | null> => {
    try {
      return await userDataPersistence.getUserData(dataId);
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'useUserDataPersistence',
        { action: 'get_user_data', dataId },
        'medium'
      );
      return null;
    }
  }, []);

  // Get all user data
  const getAllUserData = useCallback(async (
    type?: UserDataItem['type']
  ): Promise<UserDataItem[]> => {
    if (!currentUser) return [];

    try {
      return await userDataPersistence.getAllUserData(currentUser.id, type);
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'useUserDataPersistence',
        { action: 'get_all_user_data', type },
        'medium'
      );
      return [];
    }
  }, [currentUser]);

  // Force sync
  const forceSync = useCallback(async (): Promise<SyncResult> => {
    try {
      setState(prev => ({ ...prev, isSyncing: true, error: null }));
      const result = await userDataPersistence.forceSync();
      
      if (!result.success) {
        setState(prev => ({ 
          ...prev, 
          error: `Sync failed: ${result.errors.join(', ')}` 
        }));
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setState(prev => ({ ...prev, error: errorMessage }));
      throw error;
    } finally {
      setState(prev => ({ ...prev, isSyncing: false }));
    }
  }, []);

  // Validate data
  const validateData = useCallback((
    data: any,
    type: UserDataItem['type']
  ): DataValidationResult => {
    return userDataPersistence.validateUserData(data, type);
  }, []);

  // Clear all user data
  const clearAllUserData = useCallback(async (): Promise<boolean> => {
    if (!currentUser) return false;

    try {
      return await userDataPersistence.clearAllUserData(currentUser.id);
    } catch (error) {
      errorLogger.logError(
        error instanceof Error ? error : new Error(String(error)),
        'useUserDataPersistence',
        { action: 'clear_all_user_data' },
        'high'
      );
      return false;
    }
  }, [currentUser]);

  return {
    // State
    ...state,
    currentUser,
    
    // Actions
    saveUserData,
    getUserData,
    getAllUserData,
    forceSync,
    validateData,
    clearAllUserData,
    
    // Utilities
    isAuthenticated: !!currentUser,
    userId: currentUser?.id
  };
}

// Specialized hooks for different data types
export function useConditionPersistence(options?: UseUserDataPersistenceOptions) {
  const persistence = useUserDataPersistence(options);
  
  const saveCondition = useCallback(async (conditionData: any) => {
    return await persistence.saveUserData('condition', conditionData);
  }, [persistence.saveUserData]);

  const getConditions = useCallback(async () => {
    return await persistence.getAllUserData('condition');
  }, [persistence.getAllUserData]);

  return {
    ...persistence,
    saveCondition,
    getConditions
  };
}

export function useSymptomPersistence(options?: UseUserDataPersistenceOptions) {
  const persistence = useUserDataPersistence(options);
  
  const saveSymptom = useCallback(async (symptomData: any) => {
    return await persistence.saveUserData('symptom', symptomData);
  }, [persistence.saveUserData]);

  const getSymptoms = useCallback(async () => {
    return await persistence.getAllUserData('symptom');
  }, [persistence.getAllUserData]);

  return {
    ...persistence,
    saveSymptom,
    getSymptoms
  };
}

export function useMedicationPersistence(options?: UseUserDataPersistenceOptions) {
  const persistence = useUserDataPersistence(options);
  
  const saveMedication = useCallback(async (medicationData: any) => {
    return await persistence.saveUserData('medication', medicationData);
  }, [persistence.saveUserData]);

  const getMedications = useCallback(async () => {
    return await persistence.getAllUserData('medication');
  }, [persistence.getAllUserData]);

  return {
    ...persistence,
    saveMedication,
    getMedications
  };
}

export function useFoodPersistence(options?: UseUserDataPersistenceOptions) {
  const persistence = useUserDataPersistence(options);
  
  const saveFoodEntry = useCallback(async (foodData: any) => {
    return await persistence.saveUserData('food', foodData);
  }, [persistence.saveUserData]);

  const getFoodEntries = useCallback(async () => {
    return await persistence.getAllUserData('food');
  }, [persistence.getAllUserData]);

  return {
    ...persistence,
    saveFoodEntry,
    getFoodEntries
  };
}

export function useDevicePersistence(options?: UseUserDataPersistenceOptions) {
  const persistence = useUserDataPersistence(options);
  
  const saveDeviceData = useCallback(async (deviceData: any) => {
    return await persistence.saveUserData('device', deviceData);
  }, [persistence.saveUserData]);

  const getDeviceData = useCallback(async () => {
    return await persistence.getAllUserData('device');
  }, [persistence.getAllUserData]);

  return {
    ...persistence,
    saveDeviceData,
    getDeviceData
  };
}

export function useProfilePersistence(options?: UseUserDataPersistenceOptions) {
  const persistence = useUserDataPersistence(options);
  
  const saveProfile = useCallback(async (profileData: any) => {
    return await persistence.saveUserData('profile', profileData);
  }, [persistence.saveUserData]);

  const getProfile = useCallback(async () => {
    const profiles = await persistence.getAllUserData('profile');
    return profiles[0] || null; // Return first profile or null
  }, [persistence.getAllUserData]);

  return {
    ...persistence,
    saveProfile,
    getProfile
  };
} 